import React, { PureComponent } from 'react'
import ReactDOMServer from 'react-dom/server'
import {
  string, func, bool, shape, arrayOf,
} from 'prop-types'
import FormField from '@active/react-ui/FormField'
import FormError from '@active/react-ui/FormError'
import Checkbox from '@active/react-ui/Checkbox'
import CheckboxGroup from '@active/react-ui/CheckboxGroup'
import Link from '@active/react-ui/Link'
import { withI18n } from '@active/react-utils/i18n'

import './AgreementGroups.scss'
import { noop } from '../../../shared/utils/commonUtils'
import { CheckBoxError } from '../CommonError'

class ActiveConsents extends PureComponent {
  static propTypes = {
    handleCheckboxChange: func.isRequired,
    passportAPIServerHost: string.isRequired,

    countryCodes: string,
    selectedCountryCode: string,
    resetAgrees: bool,
    isSignin: bool,

    clientConfig: shape({
      waiverInfos: arrayOf(shape({})),
      ignoreActiveWaiver: bool,
      hasCustomizedWaiver: bool,
      euCountries: arrayOf(string),
      suppressActiveWaiver: bool,
      useCustomizeWaiver: bool,
      waiverAttachementObjectIds: string,
    }).isRequired,
    i18n: func.isRequired,
    onWaiverGroupsRendered: func,
    onRef: func,
    api: shape({}),
    errorMode: string,
    requiredError: string,
  }

  static defaultProps = {
    countryCodes: '',
    selectedCountryCode: '',
    isSignin: false,
    resetAgrees: false,
    onWaiverGroupsRendered: noop,
    onRef: noop,
    api: {},
    errorMode: 'inline',
    requiredError: null,
  }

  WIAVER_CONFIGS = {
    ACTIVE_TYPE: {
      id: () => 'agreeActive',
      signinLabel: () => this.getDefaultSignInLabel,
      signupLabel: () => this.getDefaultSignInLabel,
      isShow: (countrys, selectedCountry) => !this.ignoreActive() && !this.isGDPR(countrys, selectedCountry),
    },
    GDPR_TYPE: {
      id: () => 'agreeGDPR',
      signinLabel: () => this.getDefaultSignInLabel,
      signupLabel: () => this.getDefaultSignInLabel,
      isShow: (countrys, selectedCountry) => !this.ignoreActive() && this.isGDPR(countrys, selectedCountry),
    },
    PASSPORT_CLIENT_ID: {
      id: objectId => `agreeCustom${objectId}`,
      signinLabel: () => this.getDefaultSignInLabel,
      signupLabel: () => this.getDefaultSignUpLabel,
      // eslint-disable-next-line react/destructuring-assignment
      isShow: () => this.props.clientConfig.hasCustomizedWaiver,
    },
    PASSPORT_EXTERNAL_ID: {
      id: objectId => `agreeExternal${objectId}`,
      signinLabel: () => this.getDefaultSignInLabel,
      signupLabel: () => this.getDefaultSignUpLabel,
      // eslint-disable-next-line react/destructuring-assignment
      isShow: () => this.props.clientConfig.useCustomizeWaiver,
    },
  }

  constructor(props) {
    super(props)
    this.state = {
      agrees: {},
      showError: false,
    }
    this.agreementGroupList = {}
  }

  componentDidMount() {
    const { onRef } = this.props
    onRef && onRef(this)
  }

  componentDidUpdate(prevProps) {
    const { resetAgrees } = this.props
    if (resetAgrees && prevProps.resetAgrees !== resetAgrees) {
      this.state.showError = false
    }
  }

  getSignError = () => {
    const { i18n, isSignin } = this.props
    return i18n(isSignin ? 'passport.waiver.error.agree' : 'passport.waiver.error.notAgreeMsg')
  }

  getDefaultSignInLabel = () => {
    const { i18n } = this.props
    return i18n('passport.waiver.terms.active_existAccount')
  }

  getDefaultSignUpLabel = () => {
    const { i18n } = this.props
    return i18n('passport.waiver.terms.active_newAccount')
  }

  hasEUCountry = countryCodes => (countryCodes && countryCodes.length ? countryCodes.find(this.isEUCountry) : false)

  isEUCountry = countryCode => {
    const { clientConfig: { euCountries } } = this.props
    return euCountries && euCountries.indexOf(countryCode) > -1
  }

  isGDPR = (countryCodes, selectedCountryCode) => {
    let isGDPR = this.hasEUCountry((countryCodes || '').split(/,/).filter(v => v))
    isGDPR = isGDPR || this.isEUCountry(selectedCountryCode)
    return isGDPR
  }

  ignoreActive = () => {
    const { clientConfig: { suppressActiveWaiver, ignoreActiveWaiver } } = this.props
    const ignoreActive = suppressActiveWaiver != null ? suppressActiveWaiver : ignoreActiveWaiver
    return ignoreActive
  }

  displayError = showError => {
    const { api: { setError } } = this.props

    setError && setError(showError ? this.getSignError() : null)
    this.setState({ showError })
  }

  handleChange = ({ currentTarget }) => {
    const { value, checked } = currentTarget
    const signedWaiverIds = {}
    const {
      handleCheckboxChange, clientConfig: { waiverInfos },
    } = this.props
    const { agrees } = this.state
    const wi = waiverInfos || []

    const newAgrees = { ...agrees, [value]: checked }
    this.setState({ agrees: newAgrees })

    // Only for markets to return all signed waiver ids. But I think it is useless
    wi.forEach(({ waiverVersions = [] }) => waiverVersions.forEach(({ waiverId }) => {
      if (newAgrees[waiverId]) {
        signedWaiverIds[waiverId] = waiverId
      }
    }))

    handleCheckboxChange(Object.values(signedWaiverIds))

    // If all checked by event, Then remove error
    if (this.isAgreeAll()) {
      this.displayError(false)
    }
  }

  isAgreeAll = () => !Object.values(this.agreementGroupList).find(c => !c.checked)

  // eslint-disable-next-line react/no-unused-class-component-methods
  agreementValidator = () => (this.checkAgreements() ? '' : this.getSignError())

  checkAgreements = () => {
    const isAgreeAll = this.isAgreeAll()

    this.displayError(!isAgreeAll)
    return isAgreeAll
  }

  buildWaiverGroups = agrees => {
    const {
      countryCodes, clientConfig: { waiverInfos }, selectedCountryCode, isSignin, onWaiverGroupsRendered,
    } = this.props

    this.agreementGroupList = {}
    const renderedWaivers = (waiverInfos || []).sort((a, b) => {
      if (a.waiverAttachmentType < b.waiverAttachmentType) { return -1 }
      if (a.waiverAttachmentType > b.waiverAttachmentType) { return 1 }
      return 0
    }).map(({ waiverAttachmentType, waiverVersions }) => {
      const config = this.WIAVER_CONFIGS[waiverAttachmentType]
      const defaultLabel = (isSignin ? config.signinLabel : config.signupLabel)()
      return config.isShow(countryCodes, selectedCountryCode) && (waiverVersions || []).length
        ? waiverVersions.map(wv => this.buildWaiverCheck(wv, agrees[wv.waiverId], defaultLabel))
        : undefined
    })
    onWaiverGroupsRendered(renderedWaivers)
    return renderedWaivers.filter(w => w != null)
  }

  buildWaiverCheck = (wv, checked, defaultLabel) => {
    const { isSignin } = this.props

    const { waiverId } = wv
    const customLabel = isSignin ? wv.agreementText : wv.comment
    const waiverLabel = customLabel && customLabel.length ? customLabel : defaultLabel

    return (
      <div className="agreement-checkbox-container passport-checkbox" key={waiverId}>
        <Checkbox
          className="waiver-checkbox"
          name={waiverId}
          value={waiverId}
          onChange={e => this.handleChange(e, { value: waiverId })}
          checked={checked}
          aria-label={waiverLabel.replace('{0}', wv.waiverName)}
          ref={e => { if (e) this.agreementGroupList[waiverId] = e }}
        >
          <span
            value={waiverId}
            title={waiverLabel.replace('{0}', wv.waiverName)}
            className="waiver-label"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={
              { __html: waiverLabel.replace('{0}', ReactDOMServer.renderToString(this.buildWaiverLink(wv))) }
            }
          />
        </Checkbox>
      </div>
    )
  }

  buildWaiverLink = ({ waiverIdentity, locale, waiverName }) => {
    const { passportAPIServerHost } = this.props
    const waiverLink = `${passportAPIServerHost}/waiver?waiverId=${waiverIdentity}&waiverLocale=${locale}`

    return (
      <Link key={`${waiverIdentity}_waiver`} className="waiver-span" target="_blank" rel="nofollow noopener noreferrer" href={waiverLink} title={waiverName}>{waiverName}</Link>
    )
  }

  render() {
    const {
      clientConfig: { hasCustomizedWaiver, useCustomizeWaiver }, resetAgrees, onWaiverGroupsRendered, errorMode,
      requiredError,
    } = this.props

    const {
      agrees, showError,
    } = this.state

    if (resetAgrees) {
      const keys = Object.keys(agrees)
      keys.forEach(key => { agrees[key] = false })
    }

    if (this.ignoreActive() && !hasCustomizedWaiver && !useCustomizeWaiver) {
      onWaiverGroupsRendered([])
      return null
    }

    const waiverGroups = this.buildWaiverGroups(agrees)

    return (
      <div className="passport__agreement-groups">
        {errorMode === 'alert' && (
          <FormError
            theme="inline"
            showError={showError}
            errorMessage={this.getSignError()}
          />
        )}
        <FormField disabled={!waiverGroups.length} as={CheckboxGroup} requiredError={requiredError}>
          {waiverGroups}
        </FormField>
        {errorMode === 'inline' && (
          <CheckBoxError showError={showError} errMsg={this.getSignError()} />
        )}
      </div>
    )
  }
}

export default withI18n(ActiveConsents)
